import { useMemo, useState } from "react";
import { Button, EmptyState, Modal, Skeleton } from "@cntrl/ui";
import { useSpotifyCatalog, useImportSpotifyCatalog } from "../../app/hooks/useSpotifyCatalog.js";
import type { SpotifyCatalogAlbum } from "../../app/hooks/useSpotifyCatalog.js";
import styles from "./SpotifyCatalogImportModal.module.scss";

/**
 * "Import from Spotify" — bring a band's released history into their releases hub.
 *
 * The point is that a band arriving with years of output should not retype it: they claimed their Spotify
 * artist at connect, so we already know what they have put out. Import the history, then author everything
 * FUTURE here from the start.
 *
 * What an import creates is the release SHELL — title, type, date, and the album link that lets the dashboard
 * key stats to that release. The rollout itself (checklist, budget, assets, smart link) stays theirs to author,
 * which is why the copy promises a starting point rather than a reconstructed campaign.
 *
 * **Two steps, because an import can fork a release (ART-274).** A band that made a release here before it was
 * out already has it, with no Spotify album id on it — so "already imported" cannot see it, and importing it
 * would create a second copy. The server pairs each such album with the band's release of the same title
 * (`matchedReleaseId`), and pressing Import opens a REVIEW checklist first: what will be linked to an existing
 * release, and what is net new. A link can be unticked, which imports that album as a new release instead.
 * The review comes before the write rather than after it because a duplicate, once created, has to be found
 * and deleted by hand.
 */
export function SpotifyCatalogImportModal({ onClose }: { onClose: () => void }) {
  const q = useSpotifyCatalog();
  const importer = useImportSpotifyCatalog();
  const albums = useMemo(() => q.data?.albums ?? [], [q.data]);
  const status = q.data?.status;

  // Everything not already in the hub starts selected — the common intent is "bring in my catalogue", and
  // unpicking a few is less work than picking twenty.
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const importable = useMemo(() => albums.filter((a: SpotifyCatalogAlbum) => !a.alreadyImported), [albums]);
  // Memoised: this defaults to EVERY importable album, so an un-toggled picker was rebuilding a Set over the
  // whole catalogue (up to MaxAlbums = 300) on every render.
  const chosen = useMemo(
    () => selected ?? new Set(importable.map((a) => a.spotifyAlbumId!)),
    [selected, importable],
  );

  function toggle(id: string) {
    const next = new Set(chosen);
    if (!next.delete(id)) next.add(id);
    setSelected(next);
  }

  // The review step: the chosen albums split by whether they match a release the band already has. A
  // matched album is linked unless the band unticks it, which turns it back into a new release.
  const [reviewing, setReviewing] = useState(false);
  const [refused, setRefused] = useState<Set<string>>(() => new Set());
  const picked = importable.filter((a) => chosen.has(a.spotifyAlbumId!));
  const matched = picked.filter((a) => a.matchedReleaseId);
  const linking = matched.filter((a) => !refused.has(a.spotifyAlbumId!));
  const creating = picked.filter((a) => !a.matchedReleaseId || refused.has(a.spotifyAlbumId!));

  function toggleLink(id: string) {
    const next = new Set(refused);
    if (!next.delete(id)) next.add(id);
    setRefused(next);
  }

  function submit() {
    importer.mutate({
      albumIds: creating.map((a) => a.spotifyAlbumId!),
      links: linking.map((a) => ({ spotifyAlbumId: a.spotifyAlbumId!, releaseId: a.matchedReleaseId! })),
    });
  }

  const done = importer.isSuccess ? importer.data : null;

  return (
    <Modal
      title="Import from Spotify"
      onClose={onClose}
      size="md"
    >
      {q.isLoading ? (
        <div className={styles.loading} aria-hidden="true">
          <Skeleton variant="block" height={48} />
          <Skeleton variant="block" height={48} />
          <Skeleton variant="block" height={48} />
        </div>
      ) : done ? (
        <div className={styles.done}>
          <p className={styles.doneTitle}>
            {done.created} release{done.created === 1 ? "" : "s"} imported
            {(done.releasesLinked ?? 0) > 0 ? `, ${done.releasesLinked} linked` : ""}
          </p>
          <p className={styles.doneBody}>
            {done.tracksLinked} track{done.tracksLinked === 1 ? "" : "s"} linked. Add the checklist, budget and
            assets when you’re ready — this brought in the release itself, not the rollout.
          </p>
          <Button onClick={onClose}>Done</Button>
        </div>
      ) : albums.length === 0 ? (
        // Three very different reasons produce an empty list, and telling a CONNECTED band to go connect is
        // the kind of wrong-but-plausible message that sends someone debugging the wrong end for an hour.
        <EmptyState title={emptyTitle(status)} description={emptyBody(status)} />
      ) : reviewing ? (
        <>
          <p className={styles.reviewIntro}>
            Here is how your Spotify releases line up with the ones already in Artist CNTRL.
          </p>

          {matched.length > 0 && (
            <section aria-labelledby="spotify-import-matched">
              <h3 id="spotify-import-matched" className={styles.groupHead}>
                Already in your releases · {matched.length}
              </h3>
              <ul className={styles.list}>
                {matched.map((a) => {
                  const link = !refused.has(a.spotifyAlbumId!);
                  return (
                    <li key={a.spotifyAlbumId} className={styles.row}>
                      <label className={styles.label}>
                        <input
                          type="checkbox"
                          checked={link}
                          disabled={importer.isPending}
                          onChange={() => toggleLink(a.spotifyAlbumId!)}
                          aria-label={`Link ${a.title} to ${a.matchedReleaseTitle}`}
                        />
                        <AlbumCover url={a.imageUrl} />
                        <span className={styles.body}>
                          <span className={styles.title}>{a.title}</span>
                          <span className={styles.meta}>
                            {link
                              ? `Links to your release “${a.matchedReleaseTitle}”`
                              : "Not linked · will import as a new release"}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <section aria-labelledby="spotify-import-new">
            <h3 id="spotify-import-new" className={styles.groupHead}>
              New releases · {creating.length}
            </h3>
            {creating.length === 0 ? (
              <p className={styles.reviewNone}>Nothing new — every release you picked is already here.</p>
            ) : (
              <ul className={styles.list}>
                {creating.map((a) => (
                  <li key={a.spotifyAlbumId} className={styles.row}>
                    <span className={styles.label} data-static="">
                      <AlbumCover url={a.imageUrl} />
                      <span className={styles.body}>
                        <span className={styles.title}>{a.title}</span>
                        <span className={styles.meta}>{albumMeta(a)}</span>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {importer.isError && <p className={styles.error}>{importer.error.message}</p>}

          <div className={styles.actions}>
            <Button variant="ghost" onClick={() => setReviewing(false)} disabled={importer.isPending}>
              Back
            </Button>
            <Button onClick={submit} loading={importer.isPending}>
              {confirmLabel(linking.length, creating.length)}
            </Button>
          </div>
        </>
      ) : (
        <>
          <ul className={styles.list}>
            {albums.map((a: SpotifyCatalogAlbum) => (
              <li key={a.spotifyAlbumId} className={styles.row} data-imported={a.alreadyImported ? "" : undefined}>
                {/* aria-label names the row: the checkbox's own accessible name would otherwise be assembled
                    from the nested spans, which reads as "Title SINGLE · 2024 · 12 tracks" to a screen reader. */}
                <label className={styles.label} aria-label={a.title ?? undefined}>
                  <input
                    type="checkbox"
                    checked={!a.alreadyImported && chosen.has(a.spotifyAlbumId!)}
                    // Already in the hub: shown so the list matches Spotify, but not offered — importing it
                    // again would fork the band's own release.
                    disabled={a.alreadyImported || importer.isPending}
                    onChange={() => toggle(a.spotifyAlbumId!)}
                  />
                  {/* Hotlinked from Spotify, like every other imported cover: the art is the fastest way to
                      recognise your own back catalogue in a long list. Decorative — the title is right beside
                      it and the row is already named by aria-label. */}
                  <AlbumCover url={a.imageUrl} />
                  <span className={styles.body}>
                    <span className={styles.title}>{a.title}</span>
                    <span className={styles.meta}>
                      {albumMeta(a)}
                      {a.alreadyImported ? " · already imported" : ""}
                      {!a.alreadyImported && a.matchedReleaseId ? " · already in your releases" : ""}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>

          {importer.isError && <p className={styles.error}>{importer.error.message}</p>}

          <div className={styles.actions}>
            <Button onClick={() => setReviewing(true)} disabled={picked.length === 0}>
              Import {picked.length} release{picked.length === 1 ? "" : "s"}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

/** Hotlinked from Spotify, like every other imported cover: the art is the fastest way to recognise your own
 *  back catalogue in a long list. Decorative — the title is right beside it. */
function AlbumCover({ url }: { url: string | null | undefined }) {
  return url ? (
    <img className={styles.cover} src={url} alt="" loading="lazy" />
  ) : (
    <span className={styles.cover} aria-hidden="true" />
  );
}

function albumMeta(a: SpotifyCatalogAlbum): string {
  return (
    (a.releaseType ?? "").toUpperCase() +
    (a.releaseDate ? ` · ${a.releaseDate.slice(0, 4)}` : "") +
    ` · ${a.totalTracks} track${a.totalTracks === 1 ? "" : "s"}`
  );
}

/** "Link 1 · create 6", dropping a zero half so a link-only review does not read "create 0". */
function confirmLabel(links: number, creates: number): string {
  const parts: string[] = [];
  if (links > 0) parts.push(`Link ${links}`);
  if (creates > 0) parts.push(`${parts.length ? "create" : "Create"} ${creates}`);
  return parts.join(" · ") || "Import";
}

type CatalogStatus = string | null | undefined;

function emptyTitle(status: CatalogStatus): string {
  switch (status) {
    case "not-connected": return "Spotify isn’t connected";
    case "no-artist-id":
    case "spotify-not-configured": return "Can’t reach Spotify";
    default: return "Nothing to import";
  }
}

function emptyBody(status: CatalogStatus): string {
  switch (status) {
    case "not-connected":
      return "Connect your Spotify artist in Settings › Connections, then we can read your released catalogue.";
    // Server-side configuration, not anything the band can fix — say so plainly rather than sending them to a
    // settings page that will not help.
    case "no-artist-id":
      return "This band’s Spotify connection has no artist id on it, so there is nothing to look a catalogue up by.";
    case "spotify-not-configured":
      return "This server has no Spotify app credentials configured (Spotify:AppClientId / AppClientSecret).";
    default:
      return "Spotify doesn’t list any released albums or singles for your artist yet.";
  }
}
